/*
 * The form screen's Comments pane: threads anchored to fields, grouped by
 * field in page order, filtered Open or Resolved. Clicking a thread opens it
 * (and selects its field on the page); the composer replies to the open
 * thread, or starts one on the selected field. `@` mentions firm members.
 * Comments are internal: never printed and never shown in the client portal.
 */
import {
  ArrowUp,
  AtSign,
  CheckCircle2,
  CircleAlert,
  MessageSquare,
  Pencil,
  RotateCcw
} from "lucide-react";
import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type FC,
  type KeyboardEvent
} from "react";
import { toast } from "sonner";

import { MentionedText } from "@/components/mentions/MentionedText";
import {
  EMPTY_MODEL,
  fromWire,
  initials,
  peopleCandidates,
  toWire,
  type ComposerModel,
  type MentionCandidate
} from "@/components/mentions/mentions";
import {
  MentionTextarea,
  type MentionTextareaHandle
} from "@/components/mentions/MentionTextarea";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";
import { Spinner } from "@/components/ui/spinner";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useTeamMembers } from "@/features/agents/useTeamMembers";
import { cn } from "@/lib/utils";

import {
  COMMENT_FILTERS,
  commentTime,
  groupThreadsByField,
  inFilter,
  type FormCommentFilter,
  type FormCommentThread
} from "../commentsModel";
import { useFormComments } from "../hooks/useFormComments";
import {
  fieldLineLabel,
  humanizeIdentifier,
  parseFieldLine
} from "./fieldSource/fieldLine";
import type { FieldSourceItem } from "./fieldSource/types";

export type FormCommentsPanelProps = {
  /** The case's individual id (the comments API is keyed by it). */
  individualId: string;
  filledFormId: string;
  /** The field selected on the page or in a list, if any. */
  selectedFieldName: string | null;
  /** Select a field (rings it on the page and scrolls to it). */
  onSelectField: (fieldName: string) => void;
  /** The form's fields in reading order: they order the threads and label new ones. */
  fields?: FieldSourceItem[];
  /** "Part 4 · Line 13.b · Street"; defaults to one read from `fields`. */
  fieldLabel?: (fieldName: string) => string;
  /** Start a new thread on this field (the hover card's Comment). */
  draftFieldName?: string | null;
  /** The draft thread was posted or abandoned; clear `draftFieldName`. */
  onDraftDone?: () => void;
  /** The open thread, when the screen controls it (a pin or `?comment=`). */
  activeThreadId?: string | null;
  onActiveThreadChange?: (threadId: string | null) => void;
  /** Open or Resolved, when the screen controls it (its pins follow the filter). */
  filter?: FormCommentFilter;
  onFilterChange?: (filter: FormCommentFilter) => void;
};

type Person = { name: string; avatarUrl: string | null };

/** "Part 4 · Line 13.b · Street", from the widget title when the screen knows the field. */
export const defaultFieldLabel = (
  fieldName: string,
  field?: FieldSourceItem
): string => {
  const line = fieldLineLabel(field?.pdfLabel);
  const name =
    field?.formText ||
    field?.label ||
    parseFieldLine(field?.pdfLabel).name ||
    humanizeIdentifier(fieldName);
  return line ? `${line} · ${name}` : name;
};

const PersonAvatar: FC<{ person: Person }> = ({ person }) => (
  <Avatar size="xs" className="mt-px">
    {person.avatarUrl ? <AvatarImage src={person.avatarUrl} alt="" /> : null}
    <AvatarFallback className="text-xs">{initials(person.name)}</AvatarFallback>
  </Avatar>
);

const CommentItem: FC<{
  person: Person;
  at: string | null;
  body: string;
  /** The author's own opening comment: an Edit button that swaps in an editor. */
  edit?: {
    candidates: MentionCandidate[];
    onSave: (body: string) => Promise<boolean>;
  };
}> = ({ person, at, body, edit }) => {
  const [editing, setEditing] = useState<ComposerModel | null>(null);
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!edit || !editing) return;
    const next = toWire(editing).trim();
    if (!next || next === body.trim()) {
      setEditing(null);
      return;
    }
    setSaving(true);
    const ok = await edit.onSave(next);
    setSaving(false);
    if (ok) setEditing(null);
  };
  return (
    <div className="group/comment flex items-start gap-2">
      <PersonAvatar person={person} />
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <div className="flex items-baseline gap-1.5">
          <span className="truncate text-base font-medium text-fg">
            {person.name}
          </span>
          <span className="shrink-0 text-sm text-fg-3">{commentTime(at)}</span>
          <span className="flex-1" />
          {edit && !editing ? (
            <Button
              type="button"
              variant="ghost"
              size="icon-2xs"
              aria-label="Edit Comment"
              title="Edit Comment"
              className="opacity-0 group-hover/comment:opacity-100 focus-visible:opacity-100"
              onClick={(e) => {
                e.stopPropagation();
                setEditing(fromWire(body));
              }}
            >
              <Pencil />
            </Button>
          ) : null}
        </div>
        {edit && editing ? (
          <div className="flex flex-col gap-1.5 rounded-md bg-elevated px-2 py-1.5 inset-ring inset-ring-line-3">
            <MentionTextarea
              value={editing}
              onChange={setEditing}
              candidates={edit.candidates}
              groupLabel={() => "Firm Members"}
              onSubmit={() => void save()}
              disabled={saving}
              aria-label="Edit Comment"
            />
            <div className="flex justify-end gap-1">
              <Button
                type="button"
                variant="ghost"
                size="xs"
                disabled={saving}
                onClick={() => setEditing(null)}
              >
                Cancel
              </Button>
              <Button
                type="button"
                size="xs"
                disabled={saving || !toWire(editing).trim()}
                onClick={() => void save()}
              >
                Save
              </Button>
            </div>
          </div>
        ) : (
          <MentionedText text={body} className="text-base text-fg" />
        )}
      </div>
    </div>
  );
};

const valueText = (value: unknown): string =>
  value === null || value === undefined
    ? ""
    : typeof value === "string"
    ? value.trim()
    : typeof value === "boolean"
    ? value
      ? "Checked"
      : "Unchecked"
    : String(value);

const ThreadView: FC<{
  thread: FormCommentThread;
  active: boolean;
  busy: boolean;
  personOf: (uid: string, name: string | null) => Person;
  onActivate: () => void;
  onToggleResolved: () => void;
  /** Set when the reader wrote the opening comment. */
  edit?: {
    candidates: MentionCandidate[];
    onSave: (body: string) => Promise<boolean>;
  };
}> = ({
  thread,
  active,
  busy,
  personOf,
  onActivate,
  onToggleResolved,
  edit
}) => {
  const ref = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (active) ref.current?.scrollIntoView?.({ block: "nearest" });
  }, [active]);
  const was = valueText(thread.value_at_comment);
  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.target !== e.currentTarget) return;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onActivate();
    }
  };
  return (
    <div
      ref={ref}
      role="button"
      tabIndex={0}
      aria-pressed={active}
      data-thread-id={thread.id}
      onClick={onActivate}
      onKeyDown={onKeyDown}
      className={cn(
        "flex cursor-pointer flex-col gap-2 px-3 py-2.5 outline-none transition-colors duration-100 focus-visible:bg-fill-1",
        active ? "bg-fill-2" : "hover:bg-fill-1"
      )}
    >
      <CommentItem
        person={personOf(thread.author_uid, thread.author_name)}
        at={thread.created_at}
        body={thread.body}
        edit={edit}
      />
      {thread.replies.map((r) => (
        <CommentItem
          key={r.id}
          person={personOf(r.author_uid, r.author_name)}
          at={r.created_at}
          body={r.body}
        />
      ))}
      {thread.value_changed ? (
        <span className="flex items-center gap-1.5 pl-7 text-sm text-fg-3">
          <CircleAlert className="size-3.5 shrink-0 text-warn" />
          <span className="min-w-0 truncate">
            Value changed since this comment
            {was ? ` · was ${was}` : ""}
          </span>
        </span>
      ) : null}
      {thread.resolved ? (
        <span className="flex items-center gap-1.5 pl-7 text-sm text-fg-3">
          <CheckCircle2 className="size-3.5 shrink-0 text-success" />
          {[
            `Resolved by ${
              personOf(thread.resolved_by ?? "", thread.resolved_by_name).name
            }`,
            commentTime(thread.resolved_at)
          ]
            .filter(Boolean)
            .join(" · ")}
        </span>
      ) : null}
      {active ? (
        <div className="flex gap-1 pl-6">
          <Button
            type="button"
            variant="ghost"
            size="xs"
            disabled={busy}
            onClick={(e) => {
              e.stopPropagation();
              onToggleResolved();
            }}
          >
            {thread.resolved ? <RotateCcw /> : <CheckCircle2 />}
            {thread.resolved ? "Reopen" : "Resolve"}
          </Button>
        </div>
      ) : null}
    </div>
  );
};

export const FormCommentsPanel: FC<FormCommentsPanelProps> = ({
  individualId,
  filledFormId,
  selectedFieldName,
  onSelectField,
  fields,
  fieldLabel,
  draftFieldName = null,
  onDraftDone,
  activeThreadId,
  onActiveThreadChange,
  filter: filterProp,
  onFilterChange
}) => {
  const comments = useFormComments(individualId, filledFormId);
  const { members, me } = useTeamMembers();
  const [innerFilter, setInnerFilter] = useState<FormCommentFilter>("open");
  const filter = filterProp ?? innerFilter;
  const setFilter = (next: FormCommentFilter) => {
    setInnerFilter(next);
    if (next !== filter) onFilterChange?.(next);
  };
  const [innerActive, setInnerActive] = useState<string | null>(null);
  const active = activeThreadId !== undefined ? activeThreadId : innerActive;
  const setActive = (id: string | null) => {
    setInnerActive(id);
    onActiveThreadChange?.(id);
  };
  const [draft, setDraft] = useState<ComposerModel>(EMPTY_MODEL);
  const [posting, setPosting] = useState(false);
  const [resolving, setResolving] = useState<string | null>(null);
  const composerRef = useRef<MentionTextareaHandle | null>(null);

  const byName = useMemo(
    () => new Map((fields ?? []).map((f) => [f.fieldName, f])),
    [fields]
  );
  const order = useMemo(() => (fields ?? []).map((f) => f.fieldName), [fields]);
  const labelOf = (fieldName: string, stored?: string) =>
    fieldLabel?.(fieldName) ??
    (byName.has(fieldName) || !stored
      ? defaultFieldLabel(fieldName, byName.get(fieldName))
      : stored);

  const people = useMemo(() => {
    const out = new Map<string, Person>();
    for (const m of members)
      out.set(m.uid, { name: m.label, avatarUrl: m.avatarUrl ?? null });
    if (me.uid) out.set(me.uid, { name: me.name, avatarUrl: me.avatarUrl });
    return out;
  }, [members, me]);
  const personOf = (uid: string, name: string | null): Person => {
    const known = people.get(uid);
    return {
      name: name || known?.name || "A teammate",
      avatarUrl: known?.avatarUrl ?? null
    };
  };
  const candidates = useMemo(
    () => peopleCandidates(members, me.uid || null),
    [members, me.uid]
  );

  const { threads } = comments;
  const activeThread = threads.find((t) => t.id === active) ?? null;

  // A thread opened from outside (a pin, a link) shows under its own filter.
  useEffect(() => {
    if (activeThread && !inFilter(activeThread, filter))
      setFilter(activeThread.resolved ? "resolved" : "open");
  }, [activeThread]);

  // Selecting another field on the page opens its open thread, if it has
  // one, so the composer replies there; otherwise it starts a new one.
  useEffect(() => {
    if (!selectedFieldName || activeThread?.field_name === selectedFieldName)
      return;
    const open = threads.find(
      (t) => t.field_name === selectedFieldName && !t.resolved
    );
    if (open || activeThread) setActive(open?.id ?? null);
  }, [selectedFieldName]);

  const saveEdit = async (threadId: string, body: string) => {
    try {
      await comments.editBody(threadId, body);
      return true;
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Could not save the comment."
      );
      return false;
    }
  };

  // The hover card's Comment: a new thread on that field, composer focused.
  useEffect(() => {
    if (!draftFieldName) return;
    setActive(null);
    setFilter("open");
    composerRef.current?.focus();
  }, [draftFieldName]);

  const groups = useMemo(
    () => groupThreadsByField(threads, filter, order),
    [threads, filter, order]
  );

  const replyTo =
    activeThread && !draftFieldName && inFilter(activeThread, filter)
      ? activeThread
      : null;
  const newOn = replyTo ? null : draftFieldName ?? selectedFieldName;
  const targetKey = replyTo ? `reply:${replyTo.id}` : `new:${newOn ?? ""}`;
  const lastTarget = useRef(targetKey);
  useEffect(() => {
    if (lastTarget.current !== targetKey) setDraft(EMPTY_MODEL);
    lastTarget.current = targetKey;
  }, [targetKey]);

  const context = replyTo
    ? `Reply on ${labelOf(replyTo.field_name, replyTo.line_label)}`
    : newOn
    ? `New thread on ${labelOf(newOn)}`
    : "Select a field to comment on it";
  const wire = toWire(draft).trim();
  const canPost = Boolean(wire && (replyTo || newOn) && !posting);

  const post = async () => {
    if (!canPost) return;
    setPosting(true);
    try {
      if (replyTo) {
        await comments.reply(replyTo.id, wire);
      } else if (newOn) {
        const created = await comments.create({
          field_name: newOn,
          body: wire,
          page: byName.get(newOn)?.pageNumber ?? null,
          line_label: labelOf(newOn)
        });
        setFilter("open");
        setActive(created.id);
        onDraftDone?.();
      }
      setDraft(EMPTY_MODEL);
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Could not post the comment."
      );
    } finally {
      setPosting(false);
    }
  };

  const toggleResolved = async (thread: FormCommentThread) => {
    setResolving(thread.id);
    try {
      await comments.setResolved(thread.id, !thread.resolved);
      setActive(null);
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Could not update the comment."
      );
    } finally {
      setResolving(null);
    }
  };

  return (
    <div
      data-slot="form-comments-panel"
      className="flex min-h-0 flex-1 flex-col"
    >
      <div className="flex h-11 flex-none items-center gap-2 border-b border-line-1 px-3">
        <Tabs
          value={filter}
          onValueChange={(v) => {
            setFilter(v === "resolved" ? "resolved" : "open");
            setActive(null);
          }}
        >
          <TabsList aria-label="Comment Filter">
            {COMMENT_FILTERS.map((f) => (
              <TabsTrigger key={f.id} value={f.id}>
                {f.label}
                <span className="text-sm text-fg-4 tabular-nums">
                  {comments.counts[f.id]}
                </span>
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
        <span className="flex-1" />
        <span className="text-sm text-fg-3">Page Order</span>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto">
        {comments.loading ? (
          <div className="flex justify-center py-8 text-fg-3">
            <Spinner />
          </div>
        ) : comments.error ? (
          <EmptyState
            icon={MessageSquare}
            title="Comments Didn't Load"
            description={comments.error}
            action={
              <Button type="button" size="xs" onClick={comments.refetch}>
                Try Again
              </Button>
            }
          />
        ) : groups.length === 0 ? (
          <EmptyState
            icon={MessageSquare}
            title={
              filter === "open" ? "No Open Comments" : "No Resolved Comments"
            }
            description={
              filter === "open"
                ? "Select a field and write below to ask the team about its value."
                : "Resolved threads show here."
            }
          />
        ) : (
          <div className="divide-y divide-line-1">
            {groups.map((group) => (
              <section
                key={group.fieldName}
                aria-label={labelOf(group.fieldName, group.lineLabel)}
              >
                <button
                  type="button"
                  title="Show on Page"
                  onClick={() => onSelectField(group.fieldName)}
                  className={cn(
                    "flex h-8 w-full min-w-0 items-center px-3 text-left text-sm transition-colors duration-100 hover:text-fg",
                    group.fieldName === selectedFieldName
                      ? "text-fg"
                      : "text-fg-3"
                  )}
                >
                  <span className="truncate">
                    {labelOf(group.fieldName, group.lineLabel)}
                  </span>
                </button>
                {group.threads.map((thread) => (
                  <ThreadView
                    key={thread.id}
                    thread={thread}
                    active={thread.id === active}
                    busy={resolving === thread.id}
                    personOf={personOf}
                    onActivate={() => {
                      setActive(thread.id);
                      onSelectField(thread.field_name);
                    }}
                    onToggleResolved={() => void toggleResolved(thread)}
                    edit={
                      me.uid && thread.author_uid === me.uid
                        ? {
                            candidates,
                            onSave: (body) => saveEdit(thread.id, body)
                          }
                        : undefined
                    }
                  />
                ))}
              </section>
            ))}
          </div>
        )}
      </div>

      <div className="flex-none border-t border-line-1 p-3">
        <div className="flex flex-col gap-1.5 rounded-xl bg-elevated pt-2 pr-2 pb-1.5 pl-2.5 shadow-composer inset-ring inset-ring-line-3 focus-within:inset-ring-line-4">
          <MentionTextarea
            ref={composerRef}
            value={draft}
            onChange={setDraft}
            candidates={candidates}
            groupLabel={() => "Firm Members"}
            onSubmit={() => void post()}
            disabled={!replyTo && !newOn}
            aria-label="Comment"
            placeholder={
              replyTo
                ? "Reply, or type @ to mention"
                : "Comment on this field, or type @ to mention"
            }
          />
          <div className="flex items-center gap-1">
            <span className="min-w-0 flex-1 truncate text-sm text-fg-3">
              {context}
            </span>
            <Button
              type="button"
              variant="ghost"
              size="icon-xs"
              aria-label="Mention Someone"
              title="Mention Someone"
              disabled={!replyTo && !newOn}
              onClick={() => composerRef.current?.startMention()}
            >
              <AtSign />
            </Button>
            <Button
              type="button"
              size="icon-xs"
              aria-label="Post Comment"
              title="Post Comment"
              disabled={!canPost}
              onClick={() => void post()}
              className={cn(
                "rounded-full",
                canPost
                  ? "bg-fg text-elevated hover:bg-fg/85 hover:text-elevated"
                  : "bg-fill-3 text-fg-4 disabled:opacity-100"
              )}
            >
              {posting ? <Spinner className="size-3.5" /> : <ArrowUp />}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
